import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface JobSearchHistoryQuery {
  title?: string;
  description?: string;
  location?: string;
  titles?: string[];
  companies?: string[];
}

export interface JobSearchHistoryEntry {
  id: string;
  createdAt: number;
  label: string;
  query: JobSearchHistoryQuery;
}

interface JobSearchHistoryStore {
  entries: JobSearchHistoryEntry[];
  hydrated: boolean;
  addSearch: (query: JobSearchHistoryQuery) => void;
  clearHistory: () => void;
  setHydrated: (value: boolean) => void;
}

const MAX_HISTORY_ITEMS = 10;

const normalizeText = (value?: string): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const cleaned = value
    .trim()
    .replace(/\s+/g, ' ');

  return cleaned.length > 0 ? cleaned : undefined;
};

const normalizeList = (values?: string[]): string[] | undefined => {
  if (!Array.isArray(values)) {
    return undefined;
  }

  const deduped: string[] = [];
  const seen = new Set<string>();

  for (const value of values) {
    const cleaned = normalizeText(value);
    if (!cleaned) {
      continue;
    }
    const key = cleaned.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push(cleaned);
  }

  return deduped.length > 0 ? deduped : undefined;
};

const normalizeQuery = (query: JobSearchHistoryQuery): JobSearchHistoryQuery => ({
  title: normalizeText(query.title),
  description: normalizeText(query.description),
  location: normalizeText(query.location),
  titles: normalizeList(query.titles),
  companies: normalizeList(query.companies),
});

const hasQueryValues = (query: JobSearchHistoryQuery): boolean => Boolean(
  query.title ||
  query.description ||
  query.location ||
  (query.titles && query.titles.length > 0) ||
  (query.companies && query.companies.length > 0)
);

const buildQueryKey = (query: JobSearchHistoryQuery): string => JSON.stringify({
  title: query.title?.toLowerCase() || null,
  description: query.description?.toLowerCase() || null,
  location: query.location?.toLowerCase() || null,
  titles: (query.titles || []).map((value) => value.toLowerCase()),
  companies: (query.companies || []).map((value) => value.toLowerCase()),
});

const buildEntryLabel = (query: JobSearchHistoryQuery): string => {
  const parts: string[] = [];

  if (query.title) {
    parts.push(`Title: ${query.title}`);
  }
  if (query.location) {
    parts.push(`Location: ${query.location}`);
  }
  if (query.companies?.length) {
    parts.push(`Company: ${query.companies.join(', ')}`);
  }
  if (query.titles?.length) {
    parts.push(`Titles: ${query.titles.join(', ')}`);
  }
  if (query.description) {
    parts.push(`Description: ${query.description}`);
  }

  const label = parts.join(' | ');
  if (label.length <= 180) {
    return label;
  }
  return `${label.slice(0, 177)}...`;
};

export const useJobSearchHistoryStore = create(
  persist<JobSearchHistoryStore>(
    (set) => ({
      entries: [],
      hydrated: false,
      addSearch: (query) => {
        const normalizedQuery = normalizeQuery(query);
        if (!hasQueryValues(normalizedQuery)) {
          return;
        }
        const queryKey = buildQueryKey(normalizedQuery);

        const entry: JobSearchHistoryEntry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          createdAt: Date.now(),
          label: buildEntryLabel(normalizedQuery),
          query: normalizedQuery,
        };

        set((state) => ({
          entries: [
            entry,
            ...state.entries.filter((candidate) => buildQueryKey(normalizeQuery(candidate.query)) !== queryKey),
          ].slice(0, MAX_HISTORY_ITEMS),
        }));
      },
      clearHistory: () => set({ entries: [] }),
      setHydrated: (value) => set({ hydrated: value }),
    }),
    {
      name: 'job-search-history-store',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated(true);
        }
      },
    }
  )
);
